/**
 * Re-read a query each time the address changes under a persistent frame (admin-cache-invalidation-rule
 * D7; shared-client-screens row H11). A frame that stays mounted across its tabs never remounts its
 * reads, so a zero trust window alone would not fire on a tab change: a booking the admin changed
 * shows on return. The first address is the frame's own read, so nothing is re-read for it.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

export function useRereadOnPathChange(queryKey: QueryKey): void {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const enteredPath = useRef(pathname);
  // The key is read when the path changes, never as a trigger of its own.
  const keyRef = useRef(queryKey);
  keyRef.current = queryKey;
  useEffect(() => {
    if (enteredPath.current === pathname) return;
    enteredPath.current = pathname;
    void queryClient.invalidateQueries({ queryKey: keyRef.current });
  }, [pathname, queryClient]);
}
